import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Button, IconButton } from '@nexelem/components';
import { useLayoutEffect, useMemo, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GanttConfig } from '../../GanttConfig';
import { LocateProductionTask } from '../../hooks';
import { useTimelineStore } from '../../store';
import { centerElement, ganttFormatters, generateTimeSeries } from '../../utils';
import { VirtualHourUnit } from './components';
import { useDateFNSConfig, useConfig } from '@nexelem/config';
import { add, differenceInDays } from 'date-fns';
import { isCurrentGanttDateWithinRangeSelector, useGanttFilterStore } from '../../../hooks/useGanttFilterStore';

type TimelineProps = {
  startDate: Date;
  endDate: Date;
};

// Offset to align TimeMarker with Time, equal to TimeMarker width
const TIMER_MARKER_OFFSET = 3;

export const Timeline = memo(({ startDate, endDate }: TimelineProps) => {
  const timelineRef = useRef<HTMLDivElement | null>(null);

  const { modules } = useConfig();
  const TIME_OFFSET = modules.production.timelineTimeOffset;

  const COUNT_UNIT_MOVE = 3;

  const { t } = useTranslation();
  const { locale } = useDateFNSConfig();

  const currentFilter = useTimelineStore((state) => state.filters.current);
  const isCurrentDateWithinRange = useGanttFilterStore(isCurrentGanttDateWithinRangeSelector);

  const loadDaysBuffer = differenceInDays(endDate, startDate) + 1;

  const { timeSeries } = useMemo(
    () =>
      generateTimeSeries({
        scaleFilter: currentFilter,
        loadDaysBuffer,
        startDate,
        adapterLocale: locale,
        offset: TIME_OFFSET,
      }),
    [locale, currentFilter, loadDaysBuffer, startDate, TIME_OFFSET],
  );

  const centerCurrentDate = () => {
    if (isCurrentDateWithinRange) {
      centerElement(timelineRef, ganttFormatters.dateToSelector(new Date()));
    }
  };

  useLayoutEffect(() => {
    centerCurrentDate();
    // we need that only on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineRef, timeSeries.length, isCurrentDateWithinRange]);

  const handleNowClick = () => {
    centerCurrentDate();
  };

  const handleScrollTimeline = (directory: 'left' | 'right') => () => {
    const scrollContainer = document.getElementById(LocateProductionTask.scrollContainerId);

    if (scrollContainer) {
      const value = COUNT_UNIT_MOVE * GanttConfig.SINGLE_ELEMENT_WIDTH * (directory === 'left' ? -1 : 1);
      scrollContainer.scrollBy({
        left: value,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="mr-5 flex">
      <div className="fixed flex h-16.5 items-center bg-surface-primary">
        <IconButton Icon={ChevronLeftRounded} size="small" onClick={handleScrollTimeline('left')} />
      </div>
      <div
        className="flex h-16.5 bg-surface-primary text-text-secondary"
        ref={timelineRef}
        style={{
          marginLeft:
            (TIME_OFFSET * GanttConfig.SINGLE_ELEMENT_WIDTH) / currentFilter.measureScale + TIMER_MARKER_OFFSET,
        }}
      >
        {timeSeries.map(({ title, elements }) => (
          <div className="-ml-0.5 box-border flex flex-col" key={title}>
            <div className="flex grow items-center justify-center px-1 text-t14-sb text-text-primary uppercase">
              {title}
            </div>
            <div className="flex max-h-8 grow flex-nowrap justify-around border-x border-border-primary">
              {elements.map((element) => (
                <div
                  className="box-border flex h-full flex-col border-r border-border-primary"
                  data-direction={element.direction}
                  key={element.value.toISOString()}
                >
                  <span className="pl-1 text-s11-sb">{element.label}</span>
                  <div className="-ml-0.25 flex grow items-end">
                    {Array.from({
                      length: currentFilter.measureType === 'hours' ? currentFilter.measureScale : 1,
                    }).map((_, hours, arr) => (
                      <VirtualHourUnit
                        key={hours}
                        resolution={arr.length}
                        value={add(element.value, { hours: hours + TIME_OFFSET })}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="fixed right-[15px] flex h-16.5 items-center justify-between bg-surface-primary">
        <IconButton Icon={ChevronRightRounded} size="small" onClick={handleScrollTimeline('right')} />
        <div className="flex flex-nowrap items-center justify-center px-2">
          <Button disabled={!isCurrentDateWithinRange} variant="text" onClick={handleNowClick}>
            {t('components.timeline.now')}
          </Button>
        </div>
      </div>
    </div>
  );
});

Timeline.displayName = 'Timeline';
